export function money(value) {
  const formatted = decimalFixed(value, 2, { commas: true });
  return formatted === null ? "--" : `¥${formatted}`;
}

export function nav(value) {
  return decimalFixed(value, 4) ?? "--";
}

export function decimalMax4(value) {
  return decimalFixed(value, 4, { trim: true }) ?? "--";
}

export function chartNumber(value) {
  const numberValue = Number(value);
  if (!Number.isFinite(numberValue)) return "--";
  return numberValue.toLocaleString("zh-CN", { maximumFractionDigits: 4 });
}

export function chartAxisNumber(value) {
  const numberValue = Number(value);
  if (!Number.isFinite(numberValue)) return "";
  return numberValue.toLocaleString("zh-CN", { maximumFractionDigits: 2 });
}

export function percent(value, digits = 2) {
  const formatted = decimalPercent(value, digits);
  return formatted === null ? "--" : `${formatted}%`;
}

export function ratio(value) {
  return decimalFixed(value, 2) ?? "--";
}

export function decimalGteZero(value) {
  const parsed = parseDecimalParts(value);
  if (!parsed) return true;
  return !parsed.negative || parsed.allZero;
}

export function decimalSubtract(left, right) {
  const scale = 6;
  const leftValue = decimalScaledBigInt(left, scale);
  const rightValue = decimalScaledBigInt(right, scale);
  if (leftValue === null || rightValue === null) return null;
  return formatScaledBigInt(leftValue - rightValue, scale);
}

export function compactMoney(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return "--";
  const abs = Math.abs(num);
  const sign = num < 0 ? "-" : "";
  if (abs >= 100_000_000) return `${sign}¥${(abs / 100_000_000).toLocaleString("zh-CN", { maximumFractionDigits: 2 })}亿`;
  if (abs >= 10_000) return `${sign}¥${(abs / 10_000).toLocaleString("zh-CN", { maximumFractionDigits: 2 })}万`;
  return money(value);
}

export function shareUnits(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return "--";
  if (Math.abs(num) >= 10000) {
    return `${(num / 10000).toLocaleString("zh-CN", { maximumFractionDigits: 2 })} 万份`;
  }
  return `${num.toLocaleString("zh-CN", { maximumFractionDigits: 2 })} 份`;
}

export function countAssets(rows) {
  return new Set(rows.map((row) => row.asset_code).filter(Boolean)).size;
}

export function displayPortfolioName(portfolio) {
  const name = displayText(portfolio?.name).trim();
  if (!name || /\?{3,}/.test(name) || name.includes(String.fromCharCode(0xFFFD)) || name.includes(String.fromCharCode(0x951F))) {
    return "稳健红利组合";
  }
  return name;
}

// 合集状态标签:驾驶舱总览的合集列表和合集详情页共用,唯一来源放这里防止枚举文案漂移
export function collectionStatusText(status) {
  const labels = { valid: "有效", partial: "部分可用", blocked: "已阻断", empty: "空" };
  return labels[status] ?? status ?? "--";
}

export function displayAssetLabel(item) {
  const code = displayText(item?.asset_code).trim();
  const name = displayText(item?.asset_name).trim();
  if (!name || name === "--" || name === code || /\?{3,}/.test(name)) return code || "--";
  return `${name} ${code}`;
}

export function displayText(value) {
  const text = String(value ?? "");
  if (!/[ÃÂÄÅÆÇÐÑØÙÚÛÜÝÞßà-ÿ]/.test(text)) return text;
  try {
    const bytes = Uint8Array.from(text, (char) => char.charCodeAt(0) & 255);
    return new TextDecoder("utf-8", { fatal: true }).decode(bytes);
  } catch {
    return text;
  }
}

export function fmtReturnChange(vc) {
  const label = displayText(vc?.sub_label || "较上一估值日");
  if (!vc?.total_return_change) return `${label} --`;
  const formatted = decimalPercent(vc.total_return_change, 2);
  if (formatted === null) return `${label} --`;
  const sign = formatted.startsWith("-") ? "" : "+";
  return `${label} ${sign}${formatted}%`;
}

export function fmtCagrChange(vc) {
  const label = displayText(vc?.sub_label || "较上一估值日");
  if (!vc?.cagr_change_pct) return `${label} --`;
  const formatted = decimalPercent(vc.cagr_change_pct, 2);
  if (formatted === null) return `${label} --`;
  const sign = formatted.startsWith("-") ? "" : "+";
  return `${label} ${sign}${formatted}%`;
}

export function currentTime() {
  return new Date().toLocaleTimeString("zh-CN", { hour: "2-digit", minute: "2-digit" });
}

function decimalPercent(value, digits) {
  const scaled = decimalScaledBigInt(value, digits + 2);
  if (scaled === null) return null;
  return formatScaledBigInt(scaled, digits);
}

function decimalFixed(value, digits, options = {}) {
  const scaled = decimalScaledBigInt(value, digits);
  if (scaled === null) return null;
  return formatScaledBigInt(scaled, digits, options);
}

function decimalScaledBigInt(value, scale) {
  const parsed = parseDecimalParts(value);
  if (!parsed) return null;
  const padded = (parsed.fraction + "0".repeat(scale + 1)).slice(0, scale + 1);
  let scaled = BigInt(`${parsed.integer}${padded.slice(0, scale)}`);
  if ((padded[scale] ?? "0") >= "5") scaled += 1n;
  return parsed.negative ? -scaled : scaled;
}

function parseDecimalParts(value) {
  if (value === null || value === undefined || value === "") return null;
  const raw = String(value).trim().replaceAll(",", "");
  const match = raw.match(/^(-?)(\d+)(?:\.(\d+))?$/);
  if (!match) return null;
  const integer = match[2].replace(/^0+(?=\d)/, "") || "0";
  const fraction = match[3] ?? "";
  const allZero = /^0+$/.test(integer) && (fraction === "" || /^0+$/.test(fraction));
  return { negative: match[1] === "-", integer, fraction, allZero };
}

function formatScaledBigInt(value, scale, options = {}) {
  const negative = value < 0n;
  const absolute = negative ? -value : value;
  const raw = absolute.toString().padStart(scale + 1, "0");
  const integerRaw = raw.slice(0, raw.length - scale) || "0";
  const integer = options.commas ? addCommas(integerRaw) : integerRaw;
  const fractionRaw = scale > 0 ? raw.slice(-scale) : "";
  const fraction = options.trim ? fractionRaw.replace(/0+$/, "") : fractionRaw;
  return `${negative ? "-" : ""}${integer}${fraction ? `.${fraction}` : ""}`;
}

function addCommas(integer) {
  return integer.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}
